import { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import api, { apiError } from '../lib/api';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { formatIDR, formatDate, methodLabel } from '../lib/format';
import { TotalBar } from '../components/TotalBar';
import { susunTotalRekap } from '../lib/totalBar';
import { SetDicuci } from '../components/SetDicuci';
import { rapikanSetDicuci, labelRentang } from '../lib/setDicuci';

const DONUT_COLORS = ['#3B281C', '#7B5E48', '#B58A62', '#C9A96E', '#8a8574', '#5A3E28'];
const PERIODS = [
  { v: 'month', l: 'Bulan Ini' },
  { v: 'today', l: 'Hari Ini' },
  { v: '7d', l: '7 Hari' },
  { v: '30d', l: '30 Hari' },
  { v: '90d', l: '3 Bulan' },
  { v: '1y', l: '1 Tahun' },
  { v: 'custom', l: 'Custom' },
];

export default function Reports() {
  const [period, setPeriod] = useState('month');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [data, setData] = useState(null);
  const [kas, setKas] = useState(null);
  const [kasMethod, setKasMethod] = useState('');
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    if (!data) return;
    setExporting(true);
    const tid = toast.loading('Sedang membuat laporan...');
    try {
      const r = await api.get(`/export/report?start=${data.start}&end=${data.end}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Laporan_Deyran_Gallery_${data.start}_${data.end}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Laporan berhasil dibuat.', { id: tid });
    } catch (err) {
      toast.error(apiError(err), { id: tid });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (period === 'custom' && (!start || !end)) return;
    const params = new URLSearchParams({ period });
    if (period === 'custom') {
      params.set('start', start);
      params.set('end', end);
    }
    api.get(`/reports?${params}`).then((r) => setData(r.data));
  }, [period, start, end]);

  useEffect(() => {
    if (!data) return;
    api.get(`/reports/kas?start=${data.start}&end=${data.end}`).then((r) => setKas(r.data));
  }, [data]);

  const stat = (testid, label, value, sub, gelap) => (
    <div data-testid={testid} className={`rounded-2xl border shadow-soft p-4 ${gelap ? 'bg-choco border-choco text-white' : 'bg-white border-border'}`}>
      <p className={`text-[10px] uppercase tracking-wider font-semibold ${gelap ? 'text-white/70' : 'text-mocha'}`}>{label}</p>
      <p className={`font-display text-xl md:text-2xl mt-1.5 tracking-tight ${gelap ? 'text-white' : 'text-brown-dark'}`}>{value}</p>
      {sub ? <p className={`text-[10.5px] mt-0.5 leading-snug ${gelap ? 'text-white/65' : 'text-mocha'}`}>{sub}</p> : null}
    </div>
  );

  const KIND_LABEL = { dp: 'DP', deposit: 'DP', pelunasan: 'Pelunasan', pembayaran: 'Pembayaran' };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl text-ink tracking-tight">Laporan</h1>
        <button
          data-testid="report-export-button"
          onClick={doExport}
          disabled={exporting || !data}
          className="inline-flex items-center gap-2 rounded-xl bg-brown text-white px-4 py-2.5 text-sm font-semibold hover:bg-brown-med disabled:opacity-60 transition-colors"
        >
          <Download size={15} /> Export Excel
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="report-period-filters">
        {PERIODS.map((p) => (
          <button
            key={p.v}
            data-testid={`period-${p.v}`}
            onClick={() => setPeriod(p.v)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              period === p.v ? 'bg-brown text-white' : 'bg-white border border-border text-mocha hover:bg-ivory'
            }`}
          >
            {p.l}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <input data-testid="custom-start-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl border border-input bg-white px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-brown/30" />
            <span className="text-xs text-mocha">s/d</span>
            <input data-testid="custom-end-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl border border-input bg-white px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-brown/30" />
          </div>
        )}
      </div>

      {!data ? (
        <p className="text-sm text-mocha py-10 text-center">Memuat laporan…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {stat('report-revenue', 'Omset (Tgl Pakai)', formatIDR(data.revenue))}
            {stat('report-inflow', 'Uang Masuk (Tgl Bayar)', formatIDR(data.uang_masuk ?? 0))}
            {stat('report-transactions', 'Order', data.transactions)}
            {stat('report-average', 'Rata-rata Order', formatIDR(data.average))}
            {stat('report-items-rented', 'Barang Disewa (semua pcs)', `${data.items_rented} pcs`, 'termasuk bando & aksesoris tambahan')}
            {stat('report-new-customers', 'Customer Baru', data.new_customers)}
            {stat('report-set-dicuci', 'Set Dicuci (Tgl Kembali)', `${rapikanSetDicuci(data.set_dicuci).total} set`, `${rapikanSetDicuci(data.set_dicuci).setelan} setelan + ${rapikanSetDicuci(data.set_dicuci).bawahan_saja} bawahan saja`, true)}
          </div>

          <SetDicuci data={data.set_dicuci} judulPeriode={labelRentang(data.start, data.end)} />

          <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="report-chart-card">
            <h2 className="font-display text-xl text-ink mb-4">
              Omset per Tanggal Pakai — {formatDate(data.start)} s/d {formatDate(data.end)}
            </h2>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={120}>
                <AreaChart data={data.chart} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="repFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#5A3E28" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#5A3E28" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tickFormatter={(d) => formatDate(d).slice(0, 6)} tick={{ fontSize: 10, fill: '#766C64' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tickFormatter={(v) => (v >= 1000000 ? `${v / 1000000}jt` : v >= 1000 ? `${v / 1000}rb` : v)} tick={{ fontSize: 10, fill: '#766C64' }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip formatter={(v) => formatIDR(v)} labelFormatter={(d) => formatDate(d)} />
                  <Area type="monotone" dataKey="revenue" stroke="#5A3E28" strokeWidth={2.5} fill="url(#repFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="kas-report-card">
            <h2 className="font-display text-xl text-ink mb-1">Rekap Kas — Cash / Transfer / QRIS</h2>
            <p className="text-xs text-mocha mb-1">Semua pembayaran masuk pada periode ini, atas nama penyewa.</p>
            <p className="text-[11px] text-gold-dark bg-gold/10 rounded-xl px-3 py-2 mb-4" data-testid="kas-period-note">
              Catatan: Rekap ini mengikuti periode yang dipilih di atas. Dashboard "Uang Masuk" selalu menghitung kalender penuh 1 bulan (tgl 1 s/d akhir bulan). Pilih periode "Bulan Ini" agar angkanya sama persis dengan dashboard.
            </p>
            {!kas ? (
              <p className="text-xs text-mocha">Memuat rekap kas…</p>
            ) : (
              <>
                <div className="flex gap-2 flex-wrap mb-4" data-testid="kas-totals">
                  {kas.totals.map((t) => (
                    <div key={t.method} className="rounded-xl bg-ivory border border-border px-4 py-2.5">
                      <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold">{methodLabel(t.method)}</p>
                      <p className="font-display text-lg text-brown-dark" data-testid={`kas-total-${t.method.replace('+', '-')}`}>{formatIDR(t.total)}</p>
                    </div>
                  ))}
                  {kas.totals.length === 0 && <p className="text-xs text-mocha">Tidak ada pembayaran pada periode ini.</p>}
                </div>
                {kas.rows.length > 0 && (() => {
                  const byName = {};
                  for (const r of kas.rows) {
                    const e = byName[r.customer_name] || { cash: 0, transfer: 0, qris: 0, lainnya: 0 };
                    e[r.method in e ? r.method : 'lainnya'] += r.amount;
                    byName[r.customer_name] = e;
                  }
                  const names = Object.entries(byName).sort((a, b) => (b[1].cash + b[1].transfer + b[1].qris + b[1].lainnya) - (a[1].cash + a[1].transfer + a[1].qris + a[1].lainnya));
                  return (
                    <div className="mb-5" data-testid="kas-by-customer">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-mocha mb-2">Per Penyewa</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[480px]">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-mocha border-b border-border">
                              <th className="px-3 py-2 font-bold">Penyewa</th>
                              <th className="px-3 py-2 font-bold text-right">Cash</th>
                              <th className="px-3 py-2 font-bold text-right">Transfer</th>
                              <th className="px-3 py-2 font-bold text-right">QRIS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {names.map(([name, m]) => (
                              <tr key={name} data-testid={`kas-customer-${name.replace(/\s+/g, '-')}`} className="border-b border-border/60 hover:bg-ivory/60">
                                <td className="px-3 py-2 font-semibold">{name}</td>
                                <td className="px-3 py-2 text-right">{m.cash ? formatIDR(m.cash) : '-'}</td>
                                <td className="px-3 py-2 text-right">{m.transfer ? formatIDR(m.transfer) : '-'}</td>
                                <td className="px-3 py-2 text-right">{m.qris ? formatIDR(m.qris) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
                <div className="flex gap-1.5 flex-wrap mb-3" data-testid="kas-method-filters">
                  {[['', 'Semua Metode'], ['cash', 'Cash'], ['transfer', 'Transfer'], ['qris', 'QRIS']].map(([v, l]) => (
                    <button
                      key={v}
                      data-testid={`kas-filter-${v || 'semua'}`}
                      onClick={() => setKasMethod(v)}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                        kasMethod === v ? 'bg-brown text-white' : 'bg-ivory border border-border text-mocha hover:bg-white'
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                {kas.rows.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]" data-testid="kas-table">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-mocha border-b border-border">
                          <th className="px-3 py-2 font-bold">Tanggal</th>
                          <th className="px-3 py-2 font-bold">Penyewa</th>
                          <th className="px-3 py-2 font-bold">No. Order</th>
                          <th className="px-3 py-2 font-bold">Jenis</th>
                          <th className="px-3 py-2 font-bold">Metode</th>
                          <th className="px-3 py-2 font-bold text-right">Jumlah</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kas.rows.filter((r) => !kasMethod || r.method === kasMethod).map((r, i) => (
                          <tr key={i} className="border-b border-border/60 hover:bg-ivory/60">
                            <td className="px-3 py-2 whitespace-nowrap text-mocha">{formatDate(r.date)}</td>
                            <td className="px-3 py-2 font-semibold">{r.customer_name}</td>
                            <td className="px-3 py-2 text-brown font-semibold">{r.transaction_number}</td>
                            <td className="px-3 py-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.kind === 'pelunasan' ? 'bg-success/10 text-success' : 'bg-caramel/20 text-[#8a5a2e]'}`}>
                                {KIND_LABEL[r.kind] || 'Pembayaran'}
                              </span>
                            </td>
                            <td className="px-3 py-2">{methodLabel(r.method)}</td>
                            <td className="px-3 py-2 text-right font-semibold">{formatIDR(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {kas.rows.length > 0 && (() => {
                  const tr = susunTotalRekap(kas.rows, kasMethod);
                  return <TotalBar testid="report-total" className="mt-3" judul={`${tr.judul} · ${data.start ? formatDate(data.start) : ''} – ${data.end ? formatDate(data.end) : ''}`} items={tr.items} catatan={tr.catatan} />;
                })()}
              </>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="top-items-card">
              <h2 className="font-display text-xl text-ink mb-4">Barang Terlaris</h2>
              {data.top_items.length === 0 ? (
                <p className="text-xs text-mocha">Belum ada data.</p>
              ) : (
                <div className="space-y-2">
                  {data.top_items.map((i, idx) => (
                    <div key={i.code} className="flex items-center gap-3 rounded-xl bg-ivory px-4 py-2.5">
                      <span className="font-display font-bold text-caramel w-5">{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate"><span className="text-brown">{i.code}</span> {i.name}</p>
                        <p className="text-[11px] text-mocha">{i.count} pcs disewa</p>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">{formatIDR(i.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="top-categories-card">
              <h2 className="font-display text-xl text-ink mb-4">Kategori Terbaik</h2>
              {data.top_categories.length === 0 ? (
                <p className="text-xs text-mocha">Belum ada data.</p>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={120}>
                    <BarChart data={data.top_categories} layout="vertical" margin={{ left: 8, right: 8 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="category" tick={{ fontSize: 10, fill: '#29231F' }} axisLine={false} tickLine={false} width={95} />
                      <Tooltip formatter={(v) => formatIDR(v)} />
                      <Bar dataKey="revenue" fill="#B58A62" radius={[0, 8, 8, 0]} barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="report-sources-card">
              <h2 className="font-display text-xl text-ink mb-4">Sumber Order</h2>
              {data.sources.length === 0 ? (
                <p className="text-xs text-mocha">Belum ada data.</p>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="w-32 h-32 shrink-0">
                    <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={120}>
                      <PieChart>
                        <Pie data={data.sources} dataKey="value" nameKey="name" innerRadius={34} outerRadius={58} paddingAngle={3}>
                          {data.sources.map((s, i) => (
                            <Cell key={s.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-1.5">
                    {data.sources.map((s, i) => (
                      <div key={s.name} className="flex items-center gap-2 text-xs">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                        <span className="text-mocha">{s.name}</span>
                        <span className="font-bold">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
